import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const currentLang = i18n.language || 'pt';

  const navLinks = [
    { label: t('nav_home'), href: '/' },
    { label: t('nav_shopping_guide'), href: '/cssbuy-spreadsheet/' },
    { label: t('nav_reviews'), href: '/cssbuy-review/' },
    { label: t('nav_shipping'), href: '/cssbuy-shipping-cost/' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-lg md:text-xl font-bold tracking-tight text-foreground-950 whitespace-nowrap"
          >
            <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center text-white text-sm font-bold">
              C
            </span>
            CSSBuy Spreadsheet
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t('dropdown_label')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-background-200 py-2 z-50">
                  <div className="px-3 py-1.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    {t('dropdown_all')}
                  </div>
                  <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.nameKey}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-2 py-1.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-8 h-8 rounded-md overflow-hidden shrink-0">
                          <img src={cat.image} alt={cat.label} className="w-full h-full object-cover" />
                        </span>
                        <span className="truncate">{cat.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Right Side: Language + CTA */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
              {['pt', 'de', 'en'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    currentLang === lang
                      ? 'bg-white shadow-sm text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav_explore')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground-950"
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            {/* Primary Links */}
            <div className="grid grid-cols-2 gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-background-100 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-200 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Category Grid */}
            <div className="border-t border-background-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  {t('dropdown_label')}
                </div>
                <span className="text-xs text-foreground-400">{categories.length}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-background-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img src={cat.image} alt={cat.label} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <span className="absolute bottom-1.5 left-2 right-2 text-xs font-semibold text-white truncate">
                      {cat.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Language Switcher */}
            <div className="border-t border-background-200 pt-4 flex gap-2">
              {['pt', 'de', 'en'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => {
                    changeLanguage(lang);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                    currentLang === lang
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700'
                  }`}
                >
                  {lang === 'pt' ? 'PT' : lang === 'de' ? 'DE' : 'EN'}
                </button>
              ))}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-4 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg"
            >
              {t('nav_explore')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}